import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { useTrace } from '../context/TraceContext';
import { useApp } from '../context/AppContext';
import TraceLabel from '../components/trace/TraceLabel';
import ReportExportButton from '../components/export/ReportExportButton';
import ReportSection from '../components/export/ReportSection';
import { 
  FileText, GraduationCap, Plus, CheckCircle2,
  Clock, Upload
} from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const DocumentsPage = () => {
  const { pidTraceMode } = useTrace();
  const { isReadOnly } = useApp();

  const sampleSOPs = [
    { id: 'SOP-HVAC-001', title: 'HVAC System Operation and Maintenance', version: '4.0', status: 'Effective', nextReview: '2026-10-01' },
    { id: 'SOP-COMP-002', title: 'Tablet Compression Process', version: '3.2', status: 'Effective', nextReview: '2026-08-15' },
    { id: 'SOP-DISS-001', title: 'Dissolution Testing Procedure', version: '2.1', status: 'Effective', nextReview: '2026-07-01' },
    { id: 'SOP-DEV-001', title: 'Deviation Reporting and Investigation', version: '5.0', status: 'Under Review', nextReview: '2026-02-15' }
  ];

  const trainingMatrix = [
    { employee: 'Rajesh Kumar', role: 'Operator', totalSOPs: 15, completed: 14, due: 1, compliance: 93 },
    { employee: 'Priya Sharma', role: 'Production Supervisor', totalSOPs: 22, completed: 22, due: 0, compliance: 100 },
    { employee: 'Amit Patel', role: 'QC Analyst', totalSOPs: 18, completed: 17, due: 1, compliance: 94 },
    { employee: 'Kavitha Nair', role: 'Engineering', totalSOPs: 20, completed: 19, due: 1, compliance: 95 }
  ];

  return (
    <div className="space-y-6" data-testid="documents-page">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            Documents & Training
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            SOP management and training compliance tracking
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* <ReportExportButton 
            title="Documents & Training Report"
            subtitle="SOP management and training compliance tracking"
          /> */}
        </div>
      </div>

      <ReportSection id="documents-metrics" title="Documents Metrics" description="SOP and training compliance overview">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className={`bg-white border border-slate-200 shadow-sm relative ${pidTraceMode ? 'border-cyan-300' : ''}`}>
            <TraceLabel prd="PRD §6.11" req="SM-DOC-001" />
            <CardContent className="p-4">
              <p className="text-xs text-slate-600 uppercase tracking-wider mb-1 font-medium">Total SOPs</p>
              <p className="text-2xl font-bold text-slate-900">127</p>
            </CardContent>
          </Card>
          <Card className="bg-white border border-slate-200 shadow-sm">
            <CardContent className="p-4">
              <p className="text-xs text-slate-600 uppercase tracking-wider mb-1 font-medium">Under Review</p>
              <p className="text-2xl font-bold text-amber-600">8</p>
            </CardContent>
          </Card>
          <Card className="bg-white border border-slate-200 shadow-sm">
            <CardContent className="p-4">
              <p className="text-xs text-slate-600 uppercase tracking-wider mb-1 font-medium">Training Compliance</p>
              <p className="text-2xl font-bold text-emerald-600">96%</p>
            </CardContent>
          </Card>
          <Card className="bg-white border border-slate-200 shadow-sm">
            <CardContent className="p-4">
              <p className="text-xs text-slate-600 uppercase tracking-wider mb-1 font-medium">Overdue Training</p>
              <p className="text-2xl font-bold text-red-600">3</p>
            </CardContent>
          </Card>
        </div>
      </ReportSection>

      <Tabs defaultValue="documents">
        <TabsList className="bg-slate-100 border border-slate-200">
          <TabsTrigger value="documents" className="data-[state=active]:bg-[#fd4816] data-[state=active]:text-white">
            <FileText className="h-4 w-4 mr-2" />
            Documents
          </TabsTrigger>
          <TabsTrigger value="training" className="data-[state=active]:bg-[#fd4816] data-[state=active]:text-white">
            <GraduationCap className="h-4 w-4 mr-2" />
            Training
          </TabsTrigger>
        </TabsList>

        <TabsContent value="documents" className="mt-4 space-y-4">
          <div className="flex justify-end">
            {!isReadOnly && (
              <Button className="bg-[#fd4816] hover:bg-[#ea380c] text-white" data-testid="upload-document">
                <Upload className="h-4 w-4 mr-2" />
                Upload Document
              </Button>
            )}
          </div>

          <ReportSection id="sops-list" title="SOPs List" description="Standard Operating Procedures">
            <Card className={`bg-white border border-slate-200 shadow-sm relative ${pidTraceMode ? 'border-cyan-300' : ''}`}>
              <TraceLabel prd="PRD §6.11" req="SM-DOC-001" />
              <Table>
              <TableHeader>
                <TableRow className="border-slate-200 hover:bg-transparent">
                  <TableHead className="text-slate-600">ID</TableHead>
                  <TableHead className="text-slate-600">Title</TableHead>
                  <TableHead className="text-slate-600">Version</TableHead>
                  <TableHead className="text-slate-600">Status</TableHead>
                  <TableHead className="text-slate-600">Next Review</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sampleSOPs.map(sop => (
                  <TableRow key={sop.id} className="hover:bg-slate-50 cursor-pointer">
                    <TableCell className="font-mono text-xs text-slate-500">{sop.id}</TableCell>
                    <TableCell className="text-sm text-slate-800 font-medium">{sop.title}</TableCell>
                    <TableCell className="text-sm text-slate-600">v{sop.version}</TableCell>
                    <TableCell>
                      <Badge className={`${sop.status === 'Effective' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'} text-xs`}>
                        {sop.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-slate-600">{sop.nextReview}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
          </ReportSection>
        </TabsContent>

        <TabsContent value="training" className="mt-4">
          <ReportSection id="training-matrix" title="Training Compliance Matrix" description="Employee training compliance tracking">
            <Card className={`bg-white border border-slate-200 shadow-sm relative ${pidTraceMode ? 'border-cyan-300' : ''}`}>
              <TraceLabel prd="PRD §6.12" req="SM-TRN-001" />
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-slate-800">Training Compliance Matrix</CardTitle>
            </CardHeader>
            <Table>
              <TableHeader>
                <TableRow className="border-slate-200 hover:bg-transparent">
                  <TableHead className="text-slate-600">Employee</TableHead>
                  <TableHead className="text-slate-600">Role</TableHead>
                  <TableHead className="text-slate-600">Total SOPs</TableHead>
                  <TableHead className="text-slate-600">Completed</TableHead>
                  <TableHead className="text-slate-600">Due</TableHead>
                  <TableHead className="text-slate-600">Compliance</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {trainingMatrix.map((emp, idx) => (
                  <TableRow key={idx} className="hover:bg-slate-50">
                    <TableCell className="text-sm text-slate-800 font-medium">{emp.employee}</TableCell>
                    <TableCell className="text-sm text-slate-600">{emp.role}</TableCell>
                    <TableCell className="text-sm text-slate-600">{emp.totalSOPs}</TableCell>
                    <TableCell className="text-sm text-emerald-600 font-medium">{emp.completed}</TableCell>
                    <TableCell>
                      {emp.due > 0 ? (
                        <Badge className="bg-red-50 text-red-700 border-red-200 text-xs">{emp.due}</Badge>
                      ) : (
                        <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Progress value={emp.compliance} className="h-2 w-16 bg-slate-200" />
                        <span className={`text-xs font-medium ${emp.compliance >= 95 ? 'text-emerald-600' : 'text-amber-600'}`}>
                          {emp.compliance}%
                        </span>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
          </ReportSection>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default DocumentsPage;
